// Cartulina (historial de saldos) del cliente.
import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import StandardModal from "../../../components/modales/StandardModal";
import axios from "axios";
import moment from "moment-timezone";
import { EmptyState, StatCard, type DataTableColumn } from "@routes-plan/ui";
import { useT } from "../../../i18n";
import { ModalTable, ReportStats } from "../components/ReportLayout";
import { formatCurrency as companyMoney } from '../../../utils/format/currency';

type CartulinaItem = {
    id: number;
    totalValue: number;
    balance: number;
    client: {
        id: number;
        idNumber: string | number;
        fullName: string;
    };
};

export interface CartulinaModalProps {
    open: boolean;
    onClose: () => void;
    routeId: number | null;        // requerido
    date: string | null;           // YYYY-MM-DD
    routeName: string | null;
    showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const formatCurrency = (v: number) =>
    companyMoney(Number(v || 0));

const ClientPortfolioModal: React.FC<CartulinaModalProps> = ({
    open, onClose, routeId, date, routeName, showAlert, showLoading, hideLoading
}) => {
    const t = useT();
    const [items, setItems] = useState<CartulinaItem[]>([]);
    const [loading, setLoading] = useState(false);

    // anti-doble fetch (React 18 strict mode) + evitar condiciones de carrera
    const didMountRef = useRef(false);
    const reqIdRef = useRef(0);

    const rows = items.map((it, idx) => ({
        id: it.id ?? idx,                                      // DataGrid id
        clientIdNumber: it.client?.idNumber ?? "",
        clientName: it.client?.fullName ?? "",
        loanValue: it.totalValue ?? 0,
        balance: it.balance ?? 0,
        loanDate: moment(date ?? "").format("YYYY-MM-DD"),
    }));

    const sumLoan = rows.reduce((a, r) => a + Number(r.loanValue || 0), 0);
    const sumBalance = rows.reduce((a, r) => a + Number(r.balance || 0), 0);

    useEffect(() => {
        if (!open || !routeId || !date) return;

        // Permite refetch al cambiar routeId/date; evita segundo disparo del strict mode en dev
        if (!didMountRef.current) {
            didMountRef.current = true;
        }

        let active = true;
        const myReqId = ++reqIdRef.current;

        const fetchData = async () => {
            // El Cargando es el del modal (el general queda detrás).
            setLoading(true);
            try {
                const payload = {
                    routeId: [routeId],                      // siempre arreglo
                    date: moment(date).format("YYYY/MM/DD"),      // formato acordado
                };
                const { data } = await axios.post("/reports/client-history", payload);
                if (!active || myReqId !== reqIdRef.current) return;
                setItems(Array.isArray(data?.data) ? data.data : []);
            } catch (e) {
                if (active) {
                    console.error(e);
                    showAlert(t("clientReports.portfolioLoadError"), "error");
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [open, routeId, date]); // solo las dependencias necesarias

    type PortfolioRow = (typeof rows)[number];
    const columns: DataTableColumn<PortfolioRow>[] = [
        { id: "clientIdNumber", header: t("common.idNumber"), value: (r) => String(r.clientIdNumber), sortable: true },
        { id: "clientName", header: t("clientReports.client"), value: (r) => r.clientName, sortable: true },
        { id: "loanDate", header: t("clientReports.loanDate"), value: (r) => r.loanDate, hideBelow: "sm" },
        { id: "loanValue", header: t("clientReports.loanValue"), value: (r) => Number(r.loanValue ?? 0), render: (r) => formatCurrency(Number(r.loanValue ?? 0)), align: "right", sortable: true },
        { id: "balance", header: t("clientReports.balance"), value: (r) => Number(r.balance ?? 0), render: (r) => formatCurrency(Number(r.balance ?? 0)), align: "right", sortable: true },
    ];

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title={
                routeId
                    ? t("clientReports.portfolioTitleWithRoute", { date: moment(date ?? "").format("YYYY-MM-DD"), route: routeName ?? "" })
                    : t("clientReports.portfolioTitle", { date: moment(date ?? "").format("YYYY-MM-DD") })
            }
            maxWidth={900}
            loading={loading}
            actions={
                <>
                    <span />
                    <Button variant="outlined" onClick={onClose}>
                        {t("common.close")}
                    </Button>
                </>
            }
        >
            {items.length > 0 ? (
                <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
                    <ReportStats>
                        <StatCard label={t("clientReports.portfolioCount")} value={rows.length} />
                        <StatCard label={t("clientReports.loanValue")} value={formatCurrency(sumLoan)} />
                        <StatCard label={t("clientReports.balance")} value={formatCurrency(sumBalance)} />
                    </ReportStats>
                    <ModalTable
                        rows={rows}
                        columns={columns}
                        getRowId={(r) => r.id}
                        exportFileName={t("clientReports.portfolioFileName", { id: routeId ?? "" })}
                        ariaLabel={t("clientReports.portfolio")}
                    />
                </Box>
            ) : loading ? null : (
                <EmptyState dense title={t("clientReports.noPortfolioLoans")} />
            )}
        </StandardModal>
    );
};

export default ClientPortfolioModal;
